import styled from 'styled-components';

import Dropdown from 'react-bootstrap/Dropdown';

import { ReactComponent as CloseSvg } from '../../../../../../../../assets/close_icon.svg';
import { capitalize } from '../../../../../../../../utils/message';

interface Props {
  activeOptions: string[];
  options: string[];
  label: string;
  onChange: (activeOptions: string[]) => void;
  singleSelect?: boolean;
}

const MultiOptionFilter: React.FC<Props> = ({
  activeOptions,
  options,
  label,
  onChange,
  singleSelect,
}) => {
  const filteredOptions = options.filter(
    (option) => !activeOptions.includes(option),
  );

  return (
    <FilterContainer $isActive={!!activeOptions.length}>
      <DropdownToggle
        $isActive={!!activeOptions.length}
        $disabled={!filteredOptions.length}
      >
        <Background $isActive={!!activeOptions.length} />
        <span>
          {activeOptions.length
            ? activeOptions.map((option) => capitalize(option)).join(', ')
            : label}
        </span>
        {!!activeOptions.length ? (
          <CloseButton
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onChange([]);
            }}
          >
            <CloseIcon />
          </CloseButton>
        ) : null}
      </DropdownToggle>
      {filteredOptions.length ? (
        <DropdownMenu>
          {filteredOptions.map((option) => (
            <DropdownItem
              key={option}
              onClick={() => onChange([...activeOptions, option])}
            >
              {capitalize(option)}
            </DropdownItem>
          ))}
        </DropdownMenu>
      ) : null}
    </FilterContainer>
  );
};

const FilterContainer = styled(Dropdown).attrs<{ $isActive: boolean }>(
  (props) => ({
    $isActive: props.$isActive ?? false,
  }),
)`
  position: relative;
  border: 1px solid
    ${(props) =>
      props.$isActive ? 'var(--action-color)' : 'var(--border-color)'};
  transition: border-color 0.4s ease;
  border-radius: 4px;
  padding: 6px 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
`;

const Background = styled.div.attrs<{ $isActive: boolean }>((props) => ({
  $isActive: props.$isActive ?? false,
}))`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${(props) =>
    props.$isActive ? 'var(--action-color)' : 'transparent'};
  opacity: 0.15;
  transition: background-color 0.4s ease;
`;

const DropdownToggle = styled(Dropdown.Toggle).attrs<{
  $isActive: boolean;
  $disabled: boolean;
}>((props) => ({
  $isActive: props.$isActive ?? false,
  $disabled: props.$disabled ?? false,
}))`
  background-color: transparent !important;
  color: ${(props) =>
    props.$isActive ? 'var(--action-color)' : 'var(--font-color)'} !important;
  border: none !important;
  padding: 0;
  font-size: var(--font-size) !important;
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  display: flex;
  justify-content: center;
  align-items: center;

  &::after {
    display: none;
  }
`;

const DropdownMenu = styled(Dropdown.Menu)`
  padding: 0;
  margin-left: -14px;
  margin-top: 6px;
  max-height: 250px;
  overflow-y: auto;
`;

const DropdownItem = styled(Dropdown.Item)`
  height: 50px;
  display: flex;
  align-items: center;
  border-radius: 4px;
`;

const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  position: relative;
  pointer-events: all;
  height: 20px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 3px;
  margin-left: 8px;
`;

const CloseIcon = styled(CloseSvg)`
  overflow: visible;
  height: 20px;
  width: 20px;

  path {
    stroke: var(--action-color);
  }
`;

export default MultiOptionFilter;
